import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, X, CheckCheck, MessageSquare, Heart, UserPlus, Megaphone, Loader2 } from 'lucide-react'
import { useNotifications, useUnreadCount, useMarkRead } from '../shared/api/community'
import { useAuthStore } from '../shared/stores/authStore'

const typeIcons: Record<string, React.ElementType> = {
  reply: MessageSquare,
  like: Heart,
  follow: UserPlus,
  system: Megaphone,
  comment: MessageSquare,
  review: MessageSquare,
}

const NotificationBell: React.FC = () => {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { data: unreadCount = 0 } = useUnreadCount(isAuthenticated)
  const { data: notifData, isLoading } = useNotifications(isAuthenticated)
  const markRead = useMarkRead()

  const notifications = notifData?.items || []
  const hasUnread = unreadCount > 0

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!isAuthenticated) return null

  const handleMarkRead = (id?: string) => {
    markRead.mutate(id)
  }

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    if (diff < 60000) return t('community.justNow')
    if (diff < 3600000) return t('community.minutesAgo', { count: Math.floor(diff / 60000) })
    if (diff < 86400000) return t('community.hoursAgo', { count: Math.floor(diff / 3600000) })
    return d.toLocaleDateString()
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-gray-500 hover:text-[#6366f1] hover:bg-[#6366f1]/10 transition-all duration-200"
        aria-label={t('nav.notifications')}
      >
        <Bell className="w-5 h-5" />
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 z-50 max-h-96 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">{t('nav.notifications')}</h3>
            {hasUnread && (
              <button
                onClick={() => handleMarkRead()}
                className="text-xs text-[#6366f1] hover:text-[#5558e6] flex items-center gap-1"
              >
                <CheckCheck className="w-3 h-3" />
                {t('community.markAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('community.noNotifications')}</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n: any) => {
                const Icon = typeIcons[n.type] || Megaphone
                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      !n.isRead ? 'bg-[#6366f1]/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        n.type === 'like' ? 'bg-red-50' :
                        n.type === 'reply' || n.type === 'comment' ? 'bg-blue-50' :
                        n.type === 'follow' ? 'bg-green-50' : 'bg-purple-50'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          n.type === 'like' ? 'text-red-400' :
                          n.type === 'reply' || n.type === 'comment' ? 'text-blue-400' :
                          n.type === 'follow' ? 'text-green-400' : 'text-purple-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {n.type === 'like' && <Heart className="w-3 h-3 inline text-red-400 mr-0.5" />}
                          {n.title}
                        </p>
                        {n.message && (
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{n.message}</p>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-gray-400">{formatTime(n.createdAt)}</span>
                          {!n.isRead && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id) }}
                              className="text-[10px] text-[#6366f1] hover:text-[#5558e6]"
                            >
                              {t('common.read')}
                            </button>
                          )}
                        </div>
                      </div>
                      {n.link && (
                        <Link
                          to={n.link}
                          onClick={() => setOpen(false)}
                          className="shrink-0 text-gray-300 hover:text-[#6366f1]"
                        >
                          <X className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                    {!n.isRead && <div className="w-1.5 h-1.5 rounded-full bg-[#6366f1] ml-0.5 mt-1" />}
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="block text-center text-xs text-[#6366f1] py-2.5 border-t border-gray-100 hover:bg-gray-50 rounded-b-xl"
            >
              {t('community.viewAll')}
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBell
